import React, {
    FC, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';

import {
    Avatar, Button, Drawer, Popover, Spin, Tooltip, message,
} from 'antd';
import { LinkOutlined, LoadingOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash/cloneDeep';

import { useStores } from '@/components/hooks';
import { FileIcon } from '@/components/Common/Icons';
import usePolicyRequirements from '@/components/Common/hooks/usePolicyRequirements';
import hasAnyRecipientMissingDetails from '@/components/Common/MissingDetails/hasAnyRecipientMissingDetails';
import SuccessScreen from '@/components/Common/SuccessScreen';
import { checkPolicyForwardingStatus } from '@/components/FilesTable/Modals/RevocationWarningModal/helpers';
import {
    getPolicyAggregate,
    checkRequiresPhoneVerification,
    checkRequiresPasswordVerification,
    checkRequiresPasswordProtection,
    tryCopyToClipboard,
    captureUnexpectedNetworkError,
    isNestedItem,
} from '@/components/utils';
import { BASEURL, ENDPOINTS } from '@/api';
import {
    applyRules,
    ApplyRulesResponse,
    ApplyRulesResult,
    getFilePermissions,
} from '@/api/requests';
import { roleToPrimaryData, SharedUserToDisplay } from '@/stores';
import { BEUserToDisplayUser } from '@/stores/SharedUsersStore/helpers';
import {
    Policy, Recipient, RecipientRoles, SecuredFile, SharedUser,
} from '@/types/types';
import { NotifyRecipients } from '@/components/Common/SharingBlock';
import appConfig from '@/config/env';
import PermissionsView from './PermissionsView';
import AddRecipientsView from './AddRecipientsView';
import WhoHasAccess from './WhoHasAccess';
import styles from './PermissionDrawer.module.scss';


/** Keep rows from `prior` when the server list has not caught up yet (same as closeAddDrawer merge). */
const mergeServerUsersWithPrior = (
    serverUsers: SharedUserToDisplay[],
    priorUsers: SharedUserToDisplay[],
): SharedUserToDisplay[] => {
    const serverEmails = new Set(serverUsers.map((u) => u.email.toLowerCase()));
    return [
        ...serverUsers,
        ...priorUsers.filter((u) => !serverEmails.has(u.email.toLowerCase())),
    ];
};

type AddRecipientsStep = 'addRecipients' | 'selectPolicy' | 'success';

/** Mirrors add-recipients → apply rules: current shared users as rule evaluation context. */
const getRecipientsAndGroupsForApplyRules = (
    sharedUsers: SharedUserToDisplay[],
): { recipients: string[]; groups: string[] } => {
    const active = sharedUsers.filter((u) => !u.deleted);
    const recipients = active
        .filter((u) => u.recipientType === 'user' && u.email)
        .map((u) => u.email!.toLowerCase());
    const groups = active
        .filter((u) => u.recipientType === 'group')
        .map((u) => u.userId);
    return { recipients, groups };
};

export type PermissionDrawerMode = 'permissions' | 'newShare';

const nameSpace = 'filesTable.permissionDrawer';
const settingsNameSpace = 'filesTable.modals.settings';
const DRAWER_WIDTH = 604;
const MAX_VISIBLE_AVATARS = 3;
const POPOVER_SPINNER_STYLE = { fontSize: 14 };

const needsPasswordFetch = (policy: Policy): boolean => (
    !!policy && (checkRequiresPasswordVerification(policy.settings)
        || checkRequiresPasswordProtection(policy.settings))
);

interface PermissionDrawerProps {
    files: SecuredFile[];
    isOpen: boolean;
    onClose: () => void;
    mode?: PermissionDrawerMode;
    onSwitchToPermissions?: () => void;
    onSwitchToAddFlow?: () => void;
}

const PermissionDrawer: FC<PermissionDrawerProps> = observer(({
    files,
    isOpen,
    onClose,
    mode = 'permissions',
    onSwitchToPermissions,
    onSwitchToAddFlow,
}) => {
    const file = files.length === 1 ? files[0] : null;
    const isMultiFile = files.length > 1;
    const isStandaloneAddDrawer = mode === 'newShare' || isMultiFile;
    const { t } = useTranslation();
    const {
        policyStore,
        sharedUsersStore,
        authSettingsStore: { API },
        filesListStore,
        usersPhonesStore,
        userStore,
        usersListStore,
    } = useStores();
    const { usersPhonesMap } = usersPhonesStore;
    const { isMyFiles } = filesListStore;

    const [isLoading, setIsLoading] = useState(true);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isPolicyUpdating, setIsPolicyUpdating] = useState(false);
    const [users, setUsers] = useState<SharedUserToDisplay[]>([]);
    const [selectedPolicy, setSelectedPolicy] = useState<Policy>(null);

    const [perFileUsers, setPerFileUsers] = useState<Map<string, SharedUserToDisplay[]>>(new Map());
    const [folderFileCounts, setFolderFileCounts] = useState<Map<string, number>>(new Map());
    const [isLoadingPerFileUsers, setIsLoadingPerFileUsers] = useState(false);

    const [perFilePolicies, setPerFilePolicies] = useState<Map<string, string>>(new Map());

    const [ruleAssignedChildren, setRuleAssignedChildren] = useState<
        { fid: string; filename: string; policyId: string; ruleName?: string }[]
    >([]);
    const [folderChildNames, setFolderChildNames] = useState<Map<string, string>>(new Map());
    const [folderChildren, setFolderChildren] = useState<Map<string, { fid: string; filename: string }[]>>(new Map());

    const [addDrawerOpen, setAddDrawerOpen] = useState(false);
    const [addFlowSuspended, setAddFlowSuspended] = useState(false);
    const [addStep, setAddStep] = useState<AddRecipientsStep>('addRecipients');
    const [isLoadingRules, setIsLoadingRules] = useState(false);
    const [addRulesResults, setAddRulesResults] = useState<ApplyRulesResponse>([]);
    const [isSharing, setIsSharing] = useState(false);
    const [resolvedPolicyRuleName, setResolvedPolicyRuleName] = useState<string | undefined>();
    /** Server truth for policy lock / policy_id; refreshed after share changes and on load. */
    const [effectiveFile, setEffectiveFile] = useState<SecuredFile | null>(null);
    const effectiveFileRef = useRef<SecuredFile | null>(null);
    useEffect(() => {
        effectiveFileRef.current = effectiveFile;
    }, [effectiveFile]);

    const displayFile = effectiveFile ?? file;
    const isLocked = !!displayFile?.policy_set_by_rule;
    const isPolicyChangeBlocked = (appConfig.DISABLE_POLICY_CHANGES && users.length > 0)
        || isNestedItem(displayFile);

    const isPolicyChangeBlockedForFile = useCallback((targetFile: SecuredFile): boolean => {
        if (isNestedItem(targetFile)) {
            return true;
        }
        if (!appConfig.DISABLE_POLICY_CHANGES) {
            return false;
        }
        if (file?.fid === targetFile.fid) {
            return users.length > 0;
        }
        return !!perFileUsers.get(targetFile.fid)?.length;
    }, [file?.fid, perFileUsers, users.length]);

    const policyAggregate = useMemo(() => (
        selectedPolicy ? getPolicyAggregate(selectedPolicy) : {}
    ), [selectedPolicy]);

    const policyRequirements = usePolicyRequirements(selectedPolicy);

    const forwardingStatus = useMemo(() => (
        checkPolicyForwardingStatus(selectedPolicy)
    ), [selectedPolicy]);

    const { showPhoneColumn, showPasswordColumn } = useMemo(() => {
        if (!isMultiFile) {
            return {
                showPhoneColumn: policyRequirements.requiresPhone,
                showPasswordColumn: policyRequirements.requiresPassword,
            };
        }
        let phone = false;
        let password = false;
        files.forEach((f) => {
            const policy = policyStore.policyList.find((p) => p.id === (perFilePolicies.get(f.fid) || f.policy_id));
            if (!policy?.settings) return;
            if (checkRequiresPhoneVerification(policy.settings)) phone = true;
            if (checkRequiresPasswordVerification(policy.settings)
                || checkRequiresPasswordProtection(policy.settings)) password = true;
        });
        return { showPhoneColumn: phone, showPasswordColumn: password };
    }, [isMultiFile, policyRequirements, files, policyStore.policyList, perFilePolicies]);

    /** Fetch fresh file details from server, update effective file / policy state.
     *  Shared by initial open and post-action refresh paths. */
    const refreshFileDetailsAndPolicy = useCallback(async (
        source: string,
    ): Promise<SecuredFile> => {
        const fresh: SecuredFile = await API.get(
            BASEURL.backend(),
            ENDPOINTS.getFileDetails(file.fid),
            {},
        );

        const policyObj = policyStore.policyList.find((p) => p.id === fresh.policy_id) ?? null;
        const effectivePolicy = policyObj
            ? cloneDeep(policyObj)
            : fresh.policy
                ? { id: fresh.policy.id, title: fresh.policy.title, deletable: false, isMfa: false, settings: { access: [] }, active: true }
                : null;
        setSelectedPolicy(effectivePolicy);
        policyStore.setSelectedPolicy(effectivePolicy);
        setEffectiveFile(fresh);
        Object.assign(file, fresh);
        setResolvedPolicyRuleName(
            fresh.policy_set_by_rule && fresh.policy_set_by_rule_name
                ? fresh.policy_set_by_rule_name
                : undefined,
        );
        return fresh;
    }, [file, API, policyStore]);

    /** Full refresh: file details + applyRules. Called after revoke / close-add-drawer only. */
    const applyPolicyAndRulesFromServer = useCallback(async (
        sharedUsersForRules: SharedUserToDisplay[],
        source = 'applyPolicy',
    ): Promise<void> => {
        if (!file) return;
        try {
            const fresh = await refreshFileDetailsAndPolicy(source);
            const prior = effectiveFileRef.current ?? file;

            const evaluateRules = async (
                recipientsList: SharedUserToDisplay[],
            ): Promise<ApplyRulesResult[]> => {
                const { recipients, groups } = getRecipientsAndGroupsForApplyRules(recipientsList);
                return applyRules(API, {
                    file_ids: [file.fid],
                    sender: userStore.currentUserEmail,
                    recipients,
                    groups,
                });
            };

            let usersForRules = sharedUsersForRules;
            let ruleResults = await evaluateRules(usersForRules);
            let policyRule = ruleResults.find(
                (r) => r.file_id === file.fid && r.action === 'policy' && r.policy_id,
            );

            /** Prefer rules engine over file details — GET can lag right after share/permission changes. */
            const mergePolicyFromRulesAndFile = (
                rule: ApplyRulesResult | undefined,
            ): { policyId: string; lockedByRule: boolean; ruleName?: string } => {
                if (rule) {
                    return {
                        policyId: rule.policy_id,
                        lockedByRule: true,
                        ruleName: rule.rule,
                    };
                }
                return {
                    policyId: fresh.policy_id,
                    lockedByRule: fresh.policy_set_by_rule ?? false,
                    ruleName: undefined,
                };
            };

            const applyMergedToState = (
                policyId: string,
                lockedByRule: boolean,
                ruleName: string | undefined,
            ): void => {
                const mergedFile: SecuredFile = {
                    ...fresh,
                    policy_id: policyId,
                    policy_set_by_rule: lockedByRule,
                };
                const policyObj = policyStore.policyList.find((p) => p.id === policyId) ?? null;
                const effectivePolicy = policyObj
                    ? cloneDeep(policyObj)
                    : fresh.policy
                        ? { id: fresh.policy.id, title: fresh.policy.title, deletable: false, isMfa: false, settings: { access: [] }, active: true }
                        : null;
                setSelectedPolicy(effectivePolicy);
                policyStore.setSelectedPolicy(effectivePolicy);
                setEffectiveFile(mergedFile);
                Object.assign(file, mergedFile);
                setResolvedPolicyRuleName(
                    lockedByRule && ruleName ? ruleName : undefined,
                );
            };

            const first = mergePolicyFromRulesAndFile(policyRule);
            applyMergedToState(first.policyId, first.lockedByRule, first.ruleName);

            if (first.policyId !== prior.policy_id) {
                const policyForFetch = policyStore.policyList.find((p) => p.id === first.policyId) ?? null;
                await sharedUsersStore.fetchSharedUsers(
                    file.fid,
                    true,
                    needsPasswordFetch(policyForFetch),
                );
                usersForRules = mergeServerUsersWithPrior(
                    cloneDeep(sharedUsersStore.sharedUsersToDisplay),
                    usersForRules,
                );
                setUsers(usersForRules);

                ruleResults = await evaluateRules(usersForRules);
                policyRule = ruleResults.find(
                    (r) => r.file_id === file.fid && r.action === 'policy' && r.policy_id,
                );
                const second = mergePolicyFromRulesAndFile(policyRule);
                applyMergedToState(second.policyId, second.lockedByRule, second.ruleName);
            }

            await filesListStore.updateFile(file.fid, true);
        } catch (error) {
            captureUnexpectedNetworkError(error, 'PermissionDrawer.applyPolicyAndRulesFromServer');
        }
    }, [
        file,
        API,
        filesListStore,
        policyStore,
        sharedUsersStore,
        userStore,
        refreshFileDetailsAndPolicy,
    ]);

    useEffect(() => {
        if (!isOpen || !file) return;
        setEffectiveFile((prev) => (prev?.fid === file.fid ? prev : file));
    }, [isOpen, file?.fid]);

    useEffect(() => {
        if (isOpen) return;

        setUsers([]);
        setPerFileUsers(new Map());
        setFolderFileCounts(new Map());
        setFolderChildren(new Map());
        setPerFilePolicies(new Map());
        setIsLoadingPerFileUsers(false);
        setSelectedPolicy(null);
        setIsLoading(true);
        setIsUpdating(false);
        setIsPolicyUpdating(false);
        setAddDrawerOpen(false);
        setAddFlowSuspended(false);
        suspendedFromStandaloneRef.current = false;
        skipNextFetchRef.current = false;
        setAddStep('addRecipients');
        setAddRulesResults([]);
        setIsLoadingRules(false);
        setIsSharing(false);
        setResolvedPolicyRuleName(undefined);
        setEffectiveFile(null);
        sharedUsersStore.finishRecipientsFlow();
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || files.length === 0) return;

        if (skipNextFetchRef.current) {
            skipNextFetchRef.current = false;
            return;
        }

        setAddDrawerOpen(false);

        if (isMultiFile) {
            const fetchMultiData = async (): Promise<void> => {
                setIsLoading(true);
                setIsLoadingPerFileUsers(true);
                await policyStore.fetchPolicyList();

                const initPolicies = new Map<string, string>();
                files.forEach((f) => {
                    if (f.policy_id) initPolicies.set(f.fid, f.policy_id);
                });
                setPerFilePolicies(initPolicies);
                setIsLoading(false);

                const usersMap = new Map<string, SharedUserToDisplay[]>();
                const countsMap = new Map<string, number>();
                const namesMap = new Map<string, string>();
                const childrenMap = new Map<string, { fid: string; filename: string }[]>();
                await Promise.all(files.map(async (f) => {
                    try {
                        const { users: sharedUsers } = await getFilePermissions(API, f.fid);
                        usersMap.set(f.fid, sharedUsers.map(BEUserToDisplayUser));
                    } catch {
                        usersMap.set(f.fid, []);
                    }
                    if (f.is_folder) {
                        try {
                            const query = `?parent_folder=${f.fid}`;
                            const res = await API.get(BASEURL.backend(), ENDPOINTS.getFiles(query), {});
                            const items: SecuredFile[] = res?.items ?? [];
                            countsMap.set(f.fid, res?.total_count ?? items.length);
                            items.forEach((item) => namesMap.set(item.fid, item.filename));
                            childrenMap.set(f.fid, items.map((item) => ({
                                fid: item.fid,
                                filename: item.filename,
                            })));
                        } catch {
                            countsMap.set(f.fid, 0);
                        }
                    }
                }));
                setPerFileUsers(usersMap);
                setFolderFileCounts(countsMap);
                setFolderChildNames(namesMap);
                setFolderChildren(childrenMap);

                const allFileLists = [...usersMap.values()];
                if (allFileLists.length > 0) {
                    const emailSets = allFileLists.map(
                        (list) => new Set(list.map((u) => u.email.toLowerCase())),
                    );
                    const sharedWithAll = allFileLists[0].filter((u) =>
                        emailSets.every((s) => s.has(u.email.toLowerCase())),
                    );
                    sharedUsersStore.setSharedUsers(
                        sharedWithAll.map((u) => ({
                            userId: u.userId,
                            email: u.email,
                            readOnly: true,
                            actions: [],
                            deleted: false,
                            recipientType: u.recipientType,
                        })),
                        '',
                    );
                } else {
                    sharedUsersStore.setSharedUsers([], '');
                }

                setIsLoadingPerFileUsers(false);
            };
            fetchMultiData();
            return;
        }

        const fetchData = async (): Promise<void> => {
            setIsLoading(true);
            await policyStore.fetchPolicyList();

            let freshFile: SecuredFile;
            try {
                freshFile = await refreshFileDetailsAndPolicy('fetchData');
            } catch {
                freshFile = file;
            }

            const filePolicy = policyStore.policyList.find((p) => p.id === freshFile.policy_id) ?? null;

            await sharedUsersStore.fetchSharedUsers(file.fid, true, needsPasswordFetch(filePolicy));

            const displayUsers = cloneDeep(sharedUsersStore.sharedUsersToDisplay);
            setUsers(displayUsers);
            sharedUsersStore.startLinksPreloading({ filesIds: [file.fid], mode: 'general' });

            if (file.is_folder) {
                try {
                    const query = `?parent_folder=${file.fid}`;
                    const res = await API.get(BASEURL.backend(), ENDPOINTS.getFiles(query), {});
                    const items: SecuredFile[] = res?.items ?? [];
                    setFolderFileCounts(new Map([[file.fid, res?.total_count ?? items.length]]));
                    const namesMap = new Map<string, string>();
                    items.forEach((item) => namesMap.set(item.fid, item.filename));
                    setFolderChildNames(namesMap);
                    setFolderChildren(new Map([[file.fid, items.map((item) => ({
                        fid: item.fid,
                        filename: item.filename,
                    }))]]));
                    const ruleChildren = items
                        .filter((item) => item.policy_set_by_rule)
                        .map((item) => ({
                            fid: item.fid,
                            filename: item.filename,
                            policyId: item.policy_id,
                        }));
                    setRuleAssignedChildren(ruleChildren);
                } catch {
                    setFolderFileCounts(new Map([[file.fid, 0]]));
                    setFolderChildNames(new Map());
                    setRuleAssignedChildren([]);
                }
            }

            setIsLoading(false);
        };

        fetchData();
    }, [isOpen, file?.fid, isMultiFile, mode, refreshFileDetailsAndPolicy]);

    const handlePolicyChange = useCallback(async (policyId: string): Promise<void> => {
        if (isPolicyChangeBlocked) {
            return;
        }

        if (!file) return;

        const newPolicy = policyStore.policyList.find((p) => p.id === policyId);
        if (!newPolicy) return;

        const prevPolicy = cloneDeep(selectedPolicy);
        setSelectedPolicy(cloneDeep(newPolicy));
        (document.activeElement as HTMLElement)?.blur();

        setIsPolicyUpdating(true);
        try {
            await API.put(BASEURL.backend(), ENDPOINTS.filePolicy(file.fid), {
                body: { policy_id: policyId },
            });
            message.success(t(`${settingsNameSpace}.policy.messages.success.updateSuccess`, {
                filename: file.filename || file.fid,
            }));

            await sharedUsersStore.fetchSharedUsers(file.fid, true, needsPasswordFetch(newPolicy));
            setUsers(cloneDeep(sharedUsersStore.sharedUsersToDisplay));

            const fresh: SecuredFile = await API.get(
                BASEURL.backend(),
                ENDPOINTS.getFileDetails(file.fid),
                {},
            );
            const policyFromList = policyStore.policyList.find((p) => p.id === fresh.policy_id) ?? newPolicy;
            setSelectedPolicy(cloneDeep(policyFromList));
            policyStore.setSelectedPolicy(cloneDeep(policyFromList));
            setEffectiveFile(fresh);
            Object.assign(file, fresh);
            setResolvedPolicyRuleName(undefined);

            await filesListStore.updateFile(file.fid, true);
        } catch (error) {
            captureUnexpectedNetworkError(error, 'PermissionDrawer.updatePolicy');
            message.error(t(`${settingsNameSpace}.policy.messages.error.couldNotSavePolicy`, {
                filename: file.filename || file.fid,
            }));
            setSelectedPolicy(prevPolicy);
            policyStore.setSelectedPolicy(prevPolicy);
        } finally {
            setIsPolicyUpdating(false);
        }
    }, [
        file, policyStore, sharedUsersStore, API, t,
        filesListStore, selectedPolicy, isPolicyChangeBlocked,
    ]);

    const updateUserPermissions = useCallback(async (
        changedUser: { userId: string; email: string } & Partial<Pick<SharedUser, 'readOnly' | 'actions' | 'deleted'>>,
        skipRefetch = false,
        /** Role-only updates do not affect assign-policy rules; skip file details + applyRules. */
        skipPolicySync = false,
    ): Promise<void> => {
        if (!file) return;

        const fileId = file.fid;
        const payload = { ...changedUser, email: changedUser.email.toLowerCase() };

        setIsUpdating(true);
        try {
            await API.patch(BASEURL.backend(), ENDPOINTS.shareFile(fileId), {
                body: { data: [payload] },
            });
            message.success(t(`${settingsNameSpace}.shareSettings.messages.success.settingsHasBeenUpdated`));
            if (!skipRefetch) {
                await sharedUsersStore.fetchSharedUsers(fileId, true);
                const refreshed = cloneDeep(sharedUsersStore.sharedUsersToDisplay);
                setUsers(refreshed);
                if (!skipPolicySync) {
                    await applyPolicyAndRulesFromServer(refreshed, 'updateUserPermissions');
                }
            }
        } catch (error) {
            captureUnexpectedNetworkError(error, 'PermissionDrawer.updateUserPermissions');
        } finally {
            setIsUpdating(false);
        }
    }, [file, API, sharedUsersStore, t, applyPolicyAndRulesFromServer]);

    const checkIsEditable = useCallback((userId: string): boolean => (
        userId !== file?.owner_id && (isMyFiles || !!file?.menu?.edit_shares)
    ), [file, isMyFiles]);
    const canAddRecipients = isMyFiles || !!file?.menu?.edit_shares || !!file?.menu?.share;

    const changeRole = useCallback(async (email: string, recipientRole: RecipientRoles, user: SharedUserToDisplay): Promise<void> => {
        const prevUsers = users;
        setUsers((prev) => prev.map((u) => (
            u.email === email ? { ...u, recipientRole } : u
        )));

        const {
            readOnly, actions,
        } = roleToPrimaryData(recipientRole);

        try {
            await updateUserPermissions({
                userId: user.userId, email, readOnly, actions,
            }, true, true);
        } catch {
            setUsers(prevUsers);
        }
    }, [updateUserPermissions, users]);

    const handleRevoke = useCallback((email: string, user: SharedUserToDisplay): void => {
        setUsers((prev) => prev.filter((u) => u.email !== email));
        updateUserPermissions({ userId: user.userId, email, deleted: true }, false);
    }, [updateUserPermissions]);

    const handleRevokeAllDomain = useCallback(async (email: string): Promise<void> => {
        const userDomain = email.split('@')[1]?.toLowerCase();
        if (!file || !userDomain) return;

        const domainUsers = users.filter((u) => {
            const domain = u.email.split('@')[1]?.toLowerCase();
            return domain === userDomain && !u.deleted;
        });

        setUsers((prev) => prev.filter((u) => {
            const domain = u.email.split('@')[1]?.toLowerCase();
            return domain !== userDomain;
        }));

        setIsUpdating(true);
        try {
            await Promise.all(domainUsers.map((u) => (
                API.patch(BASEURL.backend(), ENDPOINTS.shareFile(file.fid), {
                    body: {
                        data: [{
                            userId: u.userId,
                            email: u.email.toLowerCase(),
                            deleted: true,
                        }],
                    },
                })
            )));
            message.success(t(`${settingsNameSpace}.shareSettings.messages.success.settingsHasBeenUpdated`));
            await sharedUsersStore.fetchSharedUsers(
                file.fid,
                true,
                needsPasswordFetch(selectedPolicy),
            );
            const refreshed = cloneDeep(sharedUsersStore.sharedUsersToDisplay);
            setUsers(refreshed);
            await applyPolicyAndRulesFromServer(refreshed, 'handleRevokeAllDomain');
        } catch (error) {
            captureUnexpectedNetworkError(error, 'PermissionDrawer.handleRevokeAllDomain');
        } finally {
            setIsUpdating(false);
        }
    }, [
        users, file, API, sharedUsersStore, t, selectedPolicy,
    ]);

    /* ─── Add Recipients Drawer ─── */

    const resetAddFlowState = useCallback((): void => {
        const addFlowEmails = sharedUsersStore.recipientsList.map((r) => r.email);
        sharedUsersStore.resetRecipientsInput(addFlowEmails);
    }, [sharedUsersStore]);

    const skipNextFetchRef = useRef(false);

    const resumeAddFlow = useCallback((): void => {
        if (suspendedFromStandaloneRef.current && onSwitchToAddFlow) {
            suspendedFromStandaloneRef.current = false;
            skipNextFetchRef.current = true;
            onSwitchToAddFlow();
        } else {
            setAddDrawerOpen(true);
        }
        setAddFlowSuspended(false);
    }, [onSwitchToAddFlow]);

    const openAddDrawer = useCallback((
        initialEmail?: string,
        initialGroup?: { id: string; name: string },
    ): void => {
        if (!addFlowSuspended) {
            resetAddFlowState();
        }
        const alreadyExists = (id: string): boolean =>
            sharedUsersStore.recipientsList.some((r) => r.id === id);

        if (initialGroup && !alreadyExists(initialGroup.id)) {
            const recipient: Recipient = {
                email: initialGroup.name,
                id: initialGroup.id,
                role: sharedUsersStore.inviteAs,
                type: 'group',
            };
            sharedUsersStore.addRecipients([recipient]);
        } else if (initialEmail && !alreadyExists(initialEmail)) {
            const recipient: Recipient = {
                email: initialEmail,
                id: initialEmail,
                role: sharedUsersStore.inviteAs,
                type: 'user',
            };
            sharedUsersStore.addRecipients([recipient]);
        }
        setAddStep('addRecipients');
        setAddRulesResults([]);
        setAddFlowSuspended(false);
        setAddDrawerOpen(true);
    }, [sharedUsersStore, resetAddFlowState, addFlowSuspended]);

    const closeAddDrawer = useCallback(async (): Promise<void> => {
        if (isStandaloneAddDrawer) {
            sharedUsersStore.clearRecipients();
            onClose();
            return;
        }

        if (!file) {
            setAddDrawerOpen(false);
            setAddFlowSuspended(false);
            setAddRulesResults([]);
            resetAddFlowState();
            return;
        }

        const ruleAssignedPolicyId = addRulesResults.find(
            (r) => r.action === 'policy'
                && r.file_id === file.fid && r.policy_id,
        )?.policy_id;
        const changedPolicyId = ruleAssignedPolicyId
            || perFilePolicies.get(file.fid);

        setAddDrawerOpen(false);
        setAddFlowSuspended(false);
        setAddRulesResults([]);
        resetAddFlowState();

        let effectivePolicy = selectedPolicy;

        if (!isPolicyChangeBlocked && changedPolicyId && changedPolicyId !== selectedPolicy?.id) {
            const newPolicy = policyStore.policyList.find(
                (p) => p.id === changedPolicyId,
            );
            if (newPolicy) {
                effectivePolicy = cloneDeep(newPolicy);
                setSelectedPolicy(effectivePolicy);
                policyStore.setSelectedPolicy(cloneDeep(newPolicy));
                filesListStore.updateFile(file.fid);
            }
        }

        await sharedUsersStore.fetchSharedUsers(
            file.fid, true, needsPasswordFetch(effectivePolicy),
        );
        const merged = mergeServerUsersWithPrior(
            cloneDeep(sharedUsersStore.sharedUsersToDisplay),
            users,
        );
        setUsers(merged);
        await applyPolicyAndRulesFromServer(merged, 'closeAddDrawer');
    }, [
        isStandaloneAddDrawer, sharedUsersStore, onClose, resetAddFlowState,
        file, selectedPolicy, perFilePolicies, addRulesResults,
        policyStore, filesListStore, users, applyPolicyAndRulesFromServer,
        isPolicyChangeBlocked,
    ]);

    const suspendedFromStandaloneRef = useRef(false);

    const handleWhoHasAccessClick = useCallback((): void => {
        if (isStandaloneAddDrawer && onSwitchToPermissions) {
            setAddFlowSuspended(true);
            suspendedFromStandaloneRef.current = true;
            onSwitchToPermissions();
        } else {
            setAddDrawerOpen(false);
            setAddFlowSuspended(true);
            suspendedFromStandaloneRef.current = false;
        }
    }, [isStandaloneAddDrawer, onSwitchToPermissions]);

    const handleNextClick = useCallback(async (): Promise<void> => {
        if (files.length === 0) return;

        const { recipientsList } = sharedUsersStore;
        const senderEmail = userStore.currentUserEmail;

        const recipients = recipientsList
            .filter((r) => r.type === 'user')
            .map((r) => r.email);
        const groups = recipientsList
            .filter((r) => r.type === 'group')
            .map((r) => r.id);

        setAddStep('selectPolicy');
        setIsLoadingRules(true);

        try {
            const results = await applyRules(API, {
                file_ids: files.map((f) => f.fid),
                sender: senderEmail,
                recipients,
                groups,
            });
            setAddRulesResults(results);

            if (isMultiFile) {
                setPerFilePolicies((prev) => {
                    const next = new Map(prev);
                    results
                        .filter((r) => r.action === 'policy' && r.policy_id)
                        .forEach((r) => next.set(r.file_id, r.policy_id));
                    return next;
                });
            }
        } catch (error) {
            captureUnexpectedNetworkError(error, 'PermissionDrawer.applyRules');
            message.error(t('general.specterxCommon.couldNotShareTryAgain'));
            setAddStep('addRecipients');
        } finally {
            setIsLoadingRules(false);
        }
    }, [files, sharedUsersStore, userStore, API, t, isMultiFile]);

    const handlePerFilePolicyChange = useCallback(async (
        fid: string,
        policyId: string,
    ): Promise<void> => {
        const targetFile = files.find((item) => item.fid === fid);
        if (targetFile && isPolicyChangeBlockedForFile(targetFile)) {
            return;
        }

        const previousId = perFilePolicies.get(fid);
        setPerFilePolicies((prev) => {
            const next = new Map(prev);
            next.set(fid, policyId);
            return next;
        });
        (document.activeElement as HTMLElement)?.blur();

        try {
            await API.put(BASEURL.backend(), ENDPOINTS.filePolicy(fid), {
                body: { policy_id: policyId },
            });
            message.success(t(`${settingsNameSpace}.policy.messages.success.updateSuccess`, {
                filename: targetFile?.filename || fid,
            }));
        } catch (error) {
            captureUnexpectedNetworkError(error, 'PermissionDrawer.updatePerFilePolicy');
            if (previousId !== undefined) {
                setPerFilePolicies((prev) => {
                    const next = new Map(prev);
                    next.set(fid, previousId);
                    return next;
                });
            }
        }
    }, [API, files, t, perFilePolicies, isPolicyChangeBlockedForFile]);

    const goBackToStep1 = useCallback((): void => {
        setAddStep('addRecipients');
        setAddRulesResults([]);
    }, []);

    const handleShare = useCallback(async (): Promise<void> => {
        if (files.length === 0) return;
        setIsSharing(true);

        const filesToShare = files.map((f) => {
            const policyId = isPolicyChangeBlockedForFile(f)
                ? f.policy_id
                : perFilePolicies.get(f.fid)
                || addRulesResults.find(
                    (r) => r.action === 'policy'
                        && r.file_id === f.fid && r.policy_id,
                )?.policy_id
                || selectedPolicy?.id
                || f.policy_id;

            return {
                file_id: f.fid,
                policy_id: policyId,
            };
        });

        try {
            await sharedUsersStore.bulkShareFiles({ files: filesToShare, sendNotification: false });

            if (file && !isMultiFile) {
                const existingEmails = new Set(
                    users.map((u) => u.email.toLowerCase()),
                );
                const newUsers: SharedUserToDisplay[] = sharedUsersStore
                    .recipientsList
                    .filter((r) => r.type === 'user'
                        && !existingEmails.has(r.email.toLowerCase()))
                    .map((r) => ({
                        userId: r.email,
                        email: r.email,
                        recipientRole: r.role || 'viewer',
                        recipientType: 'user' as const,
                    }));
                if (newUsers.length > 0) {
                    setUsers((prev) => [...prev, ...newUsers]);
                }
            }

            sharedUsersStore.cancelLinksPreloading();
            sharedUsersStore.startLinksPreloading({
                filesIds: files.map((f) => f.fid),
                mode: 'allCover',
            });
            setAddStep('success');
        } catch (error) {
            captureUnexpectedNetworkError(error, 'PermissionDrawer.handleShare');
            message.error(t('general.specterxCommon.couldNotShareTryAgain'));
        } finally {
            setIsSharing(false);
        }
    }, [
        files, file, users, selectedPolicy, addRulesResults, sharedUsersStore,
        t, isMultiFile, perFilePolicies, isPolicyChangeBlockedForFile,
    ]);

    const handleSuccessDone = useCallback(async (): Promise<void> => {
        if (isStandaloneAddDrawer) {
            sharedUsersStore.clearRecipients();
            onClose();
        } else {
            await closeAddDrawer();
        }
    }, [isStandaloneAddDrawer, closeAddDrawer, sharedUsersStore, onClose]);

    const allNewRecipientsComplete = sharedUsersStore.hasRecipients;

    const allRecipientsBlocked = useMemo(() => {
        if (!sharedUsersStore.hasRecipients || addRulesResults.length === 0) return false;
        const blockRules = addRulesResults.filter((r) => r.action === 'block');
        if (blockRules.length === 0) return false;

        const blockedEmails = new Set<string>();
        blockRules.forEach((rule) => {
            rule.recipients?.forEach((email) => blockedEmails.add(email));
            rule.groups?.forEach((g) => blockedEmails.add(g));
        });

        return sharedUsersStore.recipientsList.every(
            (r) => blockedEmails.has(r.type === 'user' ? r.email : r.id),
        );
    }, [addRulesResults, sharedUsersStore.recipientsList, sharedUsersStore.hasRecipients]);

    const hasMissingDetailsUnfilled = (() => {
        if (addStep !== 'selectPolicy') return false;

        const resolvePolicy = (fid: string): Policy | null => {
            const rule = addRulesResults.find(
                (r) => r.action === 'policy'
                    && r.file_id === fid && r.policy_id,
            );
            if (rule) {
                return policyStore.policyList.find(
                    (p) => p.id === rule.policy_id,
                ) || null;
            }
            const chosen = perFilePolicies.get(fid);
            if (chosen) {
                return policyStore.policyList.find(
                    (p) => p.id === chosen,
                ) || null;
            }
            return selectedPolicy;
        };

        let needsPhone = false;
        let needsPassword = false;
        files.forEach((f) => {
            const p = resolvePolicy(f.fid);
            if (!p?.settings) return;
            if (checkRequiresPhoneVerification(p.settings)) {
                needsPhone = true;
            }
            if (
                checkRequiresPasswordVerification(p.settings)
                || checkRequiresPasswordProtection(p.settings)
            ) {
                needsPassword = true;
            }
        });

        return hasAnyRecipientMissingDetails({
            needsPhone,
            needsPassword,
            sharedUsersStore,
            usersPhonesStore,
            usersListStore,
        });
    })();

    /* ─── Add Recipients Drawer footer ─── */

    const renderAddDrawerFooter = (): JSX.Element => {
        if (addStep === 'addRecipients') {
            return (
                <div className={styles.footer} data-testid="permissionDrawer_addFlowFooter_step1">
                    <Button
                        type="link"
                        className={styles['close-button']}
                        onClick={closeAddDrawer}
                        data-testid="permissionDrawer_addFlowCancel"
                    >
                        {t('general.buttons.cancel')}
                    </Button>
                    <div className={styles['footer-right']}>
                        <NotifyRecipients labelKey="sharingBlock.notifyRecipients.labelNew" />
                        <Button
                            type="primary"
                            className={styles['add-recipients-button']}
                            disabled={!allNewRecipientsComplete}
                            onClick={handleNextClick}
                            data-testid="permissionDrawer_addFlowNext"
                        >
                            {t(`${nameSpace}.next`)}
                        </Button>
                    </div>
                </div>
            );
        }

        if (addStep === 'selectPolicy') {
            return (
                <div className={styles.footer} data-testid="permissionDrawer_addFlowFooter_step2">
                    <Button
                        type="link"
                        className={styles['close-button']}
                        onClick={goBackToStep1}
                        disabled={isLoadingRules}
                        data-testid="permissionDrawer_addFlowBack"
                    >
                        {t('general.buttons.back')}
                    </Button>
                    <div className={styles['footer-right']}>
                        <Button
                            type="primary"
                            className={styles['add-recipients-button']}
                            disabled={isLoadingRules || isSharing || allRecipientsBlocked || hasMissingDetailsUnfilled}
                            loading={isSharing}
                            onClick={handleShare}
                            data-testid="permissionDrawer_addFlowShare"
                        >
                            {t(`${nameSpace}.share`)}
                        </Button>
                    </div>
                </div>
            );
        }

        return (
            <div className={styles.footer} data-testid="permissionDrawer_addFlowFooter_success">
                <Button
                    type="link"
                    className={styles['close-button']}
                    onClick={handleSuccessDone}
                    data-testid="permissionDrawer_addFlowDone"
                >
                    {t('uploadFiles.copyToClip.done')}
                </Button>
                <div className={styles['footer-right']}>
                    <Button
                        type="primary"
                        className={styles['add-recipients-button']}
                        icon={<LinkOutlined />}
                        onClick={() => {
                            const linkData = sharedUsersStore.shareLinks.get(
                                sharedUsersStore.singleRecipientIdentity,
                            );
                            if (linkData?.link) {
                                tryCopyToClipboard(linkData.link, t('copyLink.link'));
                            }
                        }}
                        data-testid="permissionDrawer_addFlowCopyLink"
                    >
                        {t('copyLink.copyLink')}
                    </Button>
                </div>
            </div>
        );
    };

    const renderMultiFilePopoverContent = (): JSX.Element => {
        const POPOVER_AVATAR_SIZE = 28;
        return (
            <div className={styles['multi-file-popover']} data-testid="permissionDrawer_multiFilePopover">
                {files.map((f) => {
                    const fileUsers = (perFileUsers.get(f.fid) || []).filter((u) => !u.deleted);
                    return (
                        <div key={f.fid} className={styles['multi-file-row']}>
                            <FileIcon
                                className={styles['file-icon']}
                                filename={f.filename}
                                isFolder={f.is_folder}
                            />
                            <span
                                className={styles['multi-file-name']}
                                title={f.filename}
                            >
                                {f.filename}
                            </span>
                            <div className={styles['multi-file-avatars']}>
                                {isLoadingPerFileUsers ? (
                                    <Spin indicator={<LoadingOutlined style={POPOVER_SPINNER_STYLE} />} />
                                ) : (
                                    <>
                                        {fileUsers.slice(0, MAX_VISIBLE_AVATARS).map((user) => (
                                            <Tooltip
                                                key={user.email}
                                                title={user.email}
                                                placement="bottom"
                                                color="#091F56"
                                            >
                                                <Avatar size={POPOVER_AVATAR_SIZE}>
                                                    {user.email[0]?.toUpperCase()}
                                                </Avatar>
                                            </Tooltip>
                                        ))}
                                        {fileUsers.length > MAX_VISIBLE_AVATARS && (
                                            <Tooltip
                                                placement="bottom"
                                                color="#091F56"
                                                title={(
                                                    <div>
                                                        {fileUsers.slice(MAX_VISIBLE_AVATARS).map((u) => (
                                                            <div key={u.email}>{u.email}</div>
                                                        ))}
                                                    </div>
                                                )}
                                            >
                                                <Avatar size={POPOVER_AVATAR_SIZE}>
                                                    {`+${fileUsers.length - MAX_VISIBLE_AVATARS}`}
                                                </Avatar>
                                            </Tooltip>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderAddDrawerHeader = (): JSX.Element => {
        if (isMultiFile) {
            return (
                <div className={styles.header} data-testid="permissionDrawer_addFlowHeader_multiFile">
                    <Popover
                        content={renderMultiFilePopoverContent}
                        placement="bottomLeft"
                        trigger="hover"
                        overlayClassName={styles['multi-file-popover-overlay']}
                    >
                        <span className={styles['file-name-hover']} data-testid="permissionDrawer_multiFileTitle">
                            {t(`${nameSpace}.itemCount`, {
                                count: files.length,
                                defaultValue: '{{count}} items selected',
                            })}
                        </span>
                    </Popover>
                </div>
            );
        }
        const fileCount = file?.is_folder ? folderFileCounts.get(file.fid) : undefined;
        return (
            <div className={styles.header} data-testid="permissionDrawer_addFlowHeader_singleFile">
                <FileIcon
                    className={styles['file-icon']}
                    filename={file?.filename}
                    isFolder={file?.is_folder}
                />
                <span className={styles['file-name']} data-testid="permissionDrawer_addFlowFileName">
                    {file?.filename || file?.fid}
                    {fileCount !== undefined && (
                        <span className={styles['folder-file-count']}>
                            {` | ${fileCount} ${fileCount === 1 ? 'file' : 'files'}`}
                        </span>
                    )}
                </span>
                <WhoHasAccess users={users} loading={isLoading} onClick={handleWhoHasAccessClick} />
            </div>
        );
    };

    const buildSharedFiles = (): {
        fid: string;
        filename: string;
        isFolder?: boolean;
        children?: { fid: string; filename: string; policy?: { id: string; title: string; isMfa: boolean } }[];
        policy?: { id: string; title: string; isMfa: boolean };
    }[] => {
        const { policyList } = policyStore;

        const resolvePolicy = (policyId: string) => {
            const p = policyList.find((pol) => pol.id === policyId);
            return p ? { id: p.id, title: p.title, isMfa: p.isMfa } : undefined;
        };

        return files.map((f) => {
            const resolvedPolicyId = isPolicyChangeBlockedForFile(f)
                ? f.policy_id
                : perFilePolicies.get(f.fid)
                || addRulesResults.find(
                    (r) => r.action === 'policy'
                        && r.file_id === f.fid && r.policy_id,
                )?.policy_id
                || selectedPolicy?.id
                || f.policy_id;

            const policy = resolvePolicy(resolvedPolicyId);

            const children = f.is_folder
                ? (folderChildren.get(f.fid) || []).map((child) => {
                    const ruleChild = ruleAssignedChildren.find((rc) => rc.fid === child.fid);
                    const childPolicyId = ruleChild?.policyId
                        || addRulesResults.find(
                            (r) => r.action === 'policy'
                                && r.file_id === child.fid && r.policy_id,
                        )?.policy_id;
                    return {
                        fid: child.fid,
                        filename: child.filename,
                        policy: childPolicyId ? resolvePolicy(childPolicyId) : policy,
                    };
                })
                : undefined;

            return {
                fid: f.fid,
                filename: f.filename,
                isFolder: f.is_folder,
                children,
                policy,
            };
        });
    };

    const renderAddDrawerContent = (): JSX.Element => (
        <div className={styles['permission-drawer']} data-testid="permissionDrawer_addFlow">
            {renderAddDrawerHeader()}

            {addStep === 'success' && files.length > 0 ? (
                <div
                    className={styles['add-flow-success-content']}
                    data-testid="permissionDrawer_addFlowSuccessScreen"
                >
                    <SuccessScreen
                        closeDrawer={handleSuccessDone}
                        rulesResults={addRulesResults}
                        hideFooter
                        sharedFiles={buildSharedFiles()}
                    />
                </div>
            ) : (
                <AddRecipientsView
                    files={files}
                    isMultiFile={isMultiFile}
                    showPhones={showPhoneColumn}
                    showPasswords={showPasswordColumn}
                    currentStep={addStep === 'selectPolicy' ? 1 : 0}
                    isLoadingRules={isLoadingRules}
                    rulesResults={addRulesResults}
                    selectedPolicy={selectedPolicy}
                    perFilePolicies={perFilePolicies}
                    onPerFilePolicyChange={handlePerFilePolicyChange}
                    isPolicyChangeBlockedForFile={isPolicyChangeBlockedForFile}
                    folderChildNames={folderChildNames}
                />
            )}

            {renderAddDrawerFooter()}
        </div>
    );

    if (isStandaloneAddDrawer) {
        return (
            <Drawer
                open={isOpen}
                onClose={closeAddDrawer}
                width={DRAWER_WIDTH}
                closable={false}
                destroyOnClose
                rootClassName={styles['drawer-root']}
                data-testid="permissionDrawer_standaloneDrawer"
            >
                {renderAddDrawerContent()}
            </Drawer>
        );
    }

    return (
        <Drawer
            open={isOpen}
            onClose={onClose}
            width={DRAWER_WIDTH}
            closable={false}
            destroyOnClose
            rootClassName={styles['drawer-root']}
            push={{ distance: 12 }}
            data-testid="permissionDrawer_rootDrawer"
        >
            <div className={styles['permission-drawer']} data-testid="permissionDrawer">
                <div className={styles.header} data-testid="permissionDrawer_mainHeader">
                    <FileIcon
                        className={styles['file-icon']}
                        filename={file?.filename}
                        isFolder={file?.is_folder}
                    />
                    <span className={styles['file-name']} data-testid="permissionDrawer_mainFileName">
                        {file?.filename || file?.fid}
                        {file?.is_folder && folderFileCounts.has(file.fid) && (() => {
                            const count = folderFileCounts.get(file.fid);
                            return (
                                <span className={styles['folder-file-count']}>
                                    {` | ${count} ${count === 1 ? 'file' : 'files'}`}
                                </span>
                            );
                        })()}
                    </span>
                </div>

                <PermissionsView
                    isLoading={isLoading}
                    isPolicyUpdating={isPolicyUpdating}
                    isUpdating={isUpdating}
                    isLocked={isLocked}
                    isFolder={!!file?.is_folder}
                    ruleAssignedChildren={ruleAssignedChildren}
                    selectedPolicy={selectedPolicy}
                    policyAggregate={policyAggregate}
                    showPhoneColumn={showPhoneColumn}
                    showPasswordColumn={showPasswordColumn}
                    users={users}
                    hasPolicies={policyStore.hasPolicies}
                    isPolicyChangeBlocked={isPolicyChangeBlocked}
                    usersPhonesMap={usersPhonesMap}
                    shareLinks={sharedUsersStore.shareLinks}
                    singleRecipientIdentity={sharedUsersStore.singleRecipientIdentity}
                    forwardingStatus={forwardingStatus}
                    onPolicyChange={handlePolicyChange}
                    onRoleChange={changeRole}
                    onRevoke={handleRevoke}
                    onRevokeAllDomain={handleRevokeAllDomain}
                    checkIsEditable={checkIsEditable}
                    canAddRecipients={canAddRecipients}
                    addFlowSuspended={addFlowSuspended}
                    onResumeAddFlow={resumeAddFlow}
                    onClose={onClose}
                    onAddRecipients={openAddDrawer}
                    assignPolicyRuleName={resolvedPolicyRuleName}
                />
            </div>

            {addDrawerOpen && (
                <Drawer
                    open={addDrawerOpen}
                    onClose={closeAddDrawer}
                    width={DRAWER_WIDTH}
                    closable={false}
                    destroyOnClose
                    rootClassName={styles['drawer-root']}
                    data-testid="permissionDrawer_addRecipientsDrawer"
                >
                    {renderAddDrawerContent()}
                </Drawer>
            )}
        </Drawer>
    );
});

export default PermissionDrawer;
